import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, MapPin, Save } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const DEFAULTS = {
  enabled: false,
  interval_minutes: 30,
  window_start: '08:00',
  window_end: '18:00',
  target_roles: ['agent', 'admin'],
};

export default function LocationPingSettings() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pings, setPings] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cfgRes, pingsRes] = await Promise.all([
        api.get('/settings/location-ping'),
        api.get('/location-pings/latest').catch(() => ({ data: [] })),
      ]);
      setCfg({ ...DEFAULTS, ...cfgRes.data });
      setPings(pingsRes.data || []);
    } catch (err) {
      toast.error('Failed to load location ping settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      const res = await api.patch('/settings/location-ping', {
        enabled: cfg.enabled,
        interval_minutes: Number(cfg.interval_minutes),
        window_start: cfg.window_start,
        window_end: cfg.window_end,
        target_roles: cfg.target_roles,
      });
      setCfg({ ...DEFAULTS, ...res.data });
      toast.success('Location ping settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setCfg((prev) => {
      const has = (prev.target_roles || []).includes(role);
      return {
        ...prev,
        target_roles: has
          ? prev.target_roles.filter((r) => r !== role)
          : [...(prev.target_roles || []), role],
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <MapPin className="w-5 h-5 text-cyan-500" />
            Location Ping
          </CardTitle>
          <CardDescription>
            Periodically record the location of agents and admins while they're on shift. Disabled by default for privacy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div>
              <Label htmlFor="lp-enabled" className="text-sm font-medium text-slate-800">
                Enable location ping
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                When enabled, affected users' browsers will ping their coordinates every {cfg.interval_minutes} minutes during the configured window.
              </p>
            </div>
            <Switch
              id="lp-enabled"
              checked={!!cfg.enabled}
              onCheckedChange={(v) => setCfg((p) => ({ ...p, enabled: v }))}
              data-testid="lp-enabled-switch"
            />
          </div>

          {/* Interval */}
          <div>
            <Label htmlFor="lp-interval" className="text-sm text-slate-700">
              Interval (minutes)
            </Label>
            <Input
              id="lp-interval"
              type="number"
              min={5}
              max={180}
              value={cfg.interval_minutes}
              onChange={(e) => setCfg((p) => ({ ...p, interval_minutes: e.target.value }))}
              className="mt-1 max-w-[200px]"
              data-testid="lp-interval-input"
            />
            <p className="text-xs text-slate-500 mt-1">Between 5 and 180 minutes. Default: 30.</p>
          </div>

          {/* Time window */}
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div>
              <Label htmlFor="lp-start" className="text-sm text-slate-700">Ping window start</Label>
              <Input
                id="lp-start"
                type="time"
                value={cfg.window_start}
                onChange={(e) => setCfg((p) => ({ ...p, window_start: e.target.value }))}
                className="mt-1"
                data-testid="lp-start-input"
              />
            </div>
            <div>
              <Label htmlFor="lp-end" className="text-sm text-slate-700">Ping window end</Label>
              <Input
                id="lp-end"
                type="time"
                value={cfg.window_end}
                onChange={(e) => setCfg((p) => ({ ...p, window_end: e.target.value }))}
                className="mt-1"
                data-testid="lp-end-input"
              />
            </div>
          </div>

          {/* Target roles */}
          <div>
            <Label className="text-sm text-slate-700">Target roles</Label>
            <div className="flex items-center gap-4 mt-2">
              {['agent', 'admin'].map((role) => (
                <label key={role} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={(cfg.target_roles || []).includes(role)}
                    onCheckedChange={() => toggleRole(role)}
                    data-testid={`lp-role-${role}`}
                  />
                  <span className="text-sm text-slate-700 capitalize">{role}s</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">Customers are never pinged.</p>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="lp-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Latest pings */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-base">Latest location per team member</CardTitle>
          <CardDescription>
            Click a coordinate to open it in Google Maps.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {pings.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="lp-empty">
              No pings recorded yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left font-medium text-slate-500 p-3">User</th>
                  <th className="text-left font-medium text-slate-500 p-3">When</th>
                  <th className="text-left font-medium text-slate-500 p-3">Coordinates</th>
                  <th className="text-left font-medium text-slate-500 p-3">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {pings.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`lp-ping-${p.user_id}`}>
                    <td className="p-3">
                      <div className="font-medium text-slate-800">{p.user_name}</div>
                      <div className="text-xs text-slate-400 capitalize">{p.user_role}</div>
                    </td>
                    <td className="p-3 text-slate-600">
                      {new Date(p.timestamp).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs text-cyan-600 hover:underline"
                      >
                        {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
                      </a>
                    </td>
                    <td className="p-3 text-slate-500 text-xs">
                      ±{Math.round(p.accuracy || 0)}m
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
